import React, { useState, useMemo, useEffect } from 'react';
import { TrendingUp, Save, Loader2, CheckCircle2, XCircle, HelpCircle, Phone, Mail, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CHANNEL_OPTIONS = [
  { value: '', label: '— bitte wählen —' },
  { value: 'brief', label: 'Brief allein' },
  { value: 'anruf', label: 'Direktanruf allein' },
  { value: 'brief_anruf', label: 'Brief → Anruf-Nachfass' },
  { value: 'anruf_brief', label: 'Anruf → Brief auf Wunsch' },
  { value: 'email', label: 'E-Mail' },
  { value: 'linkedin', label: 'LinkedIn / Xing' },
  { value: 'empfehlung', label: 'Empfehlung' },
];

export const RESPONSE_TYPE_OPTIONS = [
  { value: '',           label: '— bitte wählen —' },
  { value: 'anruf',      label: 'Anruf' },
  { value: 'email',      label: 'E-Mail' },
  { value: 'brief',      label: 'Brief' },
  { value: 'persoenlich',label: 'Persönlich' },
];

export const QUALITY_OPTIONS = [
  { value: '',                  label: '— bitte wählen —' },
  { value: 'interesse',         label: 'Interesse — Termin gewünscht' },
  { value: 'weiterleitung',     label: 'Weiterleitung an interne Stelle' },
  { value: 'hoeflich_abgesagt', label: 'Höflich abgesagt' },
  { value: 'kein_interesse',    label: 'Kein Interesse' },
  { value: 'falsche_adresse',   label: 'Empfänger nicht zuständig' },
];

const OutcomePanel = ({ lead, password, onChange }) => {
  const [channel, setChannel] = useState(lead.first_contact_channel || '');
  const [contactDate, setContactDate] = useState(lead.first_contact_date || '');
  const [responseStatus, setResponseStatus] = useState(
    lead.response_received === true ? 'yes' :
    lead.response_received === false ? 'no' : 'unknown'
  );
  const [responseType, setResponseType] = useState(lead.response_type || '');
  const [responseDate, setResponseDate] = useState(lead.response_date || '');
  const [responseQuality, setResponseQuality] = useState(lead.response_quality || '');
  const [saving, setSaving] = useState(false);

  // Re-sync when the underlying lead reloads (parent fetches fresh data).
  useEffect(() => {
    setChannel(lead.first_contact_channel || '');
    setContactDate(lead.first_contact_date || '');
    setResponseStatus(
      lead.response_received === true ? 'yes' :
      lead.response_received === false ? 'no' : 'unknown'
    );
    setResponseType(lead.response_type || '');
    setResponseDate(lead.response_date || '');
    setResponseQuality(lead.response_quality || '');
  }, [lead]);

  const headers = useMemo(
    () => ({ 'x-admin-password': password, 'Content-Type': 'application/json' }),
    [password]
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        first_contact_channel: channel,
        first_contact_date: contactDate,
        response_received: responseStatus === 'yes' ? true : responseStatus === 'no' ? false : null,
        response_type: responseStatus === 'yes' ? responseType : '',
        response_date: responseStatus === 'yes' ? responseDate : '',
        response_quality: responseStatus === 'yes' ? responseQuality : '',
      };
      const res = await fetch(
        `${API}/admin/outreach/targets/${lead.id}/outcome`,
        { method: 'PUT', headers, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Speichern fehlgeschlagen');
      }
      onChange?.();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const channelIcon =
    channel === 'brief' ? <Send size={11} /> :
    channel === 'anruf' ? <Phone size={11} /> :
    channel === 'email' ? <Mail size={11} /> : null;

  const daysBadge = lead.days_to_response != null ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
      ⏱ {lead.days_to_response} Tage bis Antwort
    </span>
  ) : null;

  return (
    <section
      className="bg-emerald-50/40 border border-emerald-200/60 rounded-lg p-4"
      data-testid="lead-outcome-section"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
        <TrendingUp size={14} className="text-emerald-600" />
        Resonanz auf Erstkontakt
        {channel && channelIcon}
        {daysBadge}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1 block">
            Erstkontakt-Kanal
          </label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
            data-testid="outcome-channel"
          >
            {CHANNEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1 block">
            Erstkontakt am
          </label>
          <Input
            type="date"
            value={contactDate}
            onChange={(e) => setContactDate(e.target.value)}
            className="bg-white"
            data-testid="outcome-contact-date"
          />
        </div>
      </div>

      {/* Response status — three radio chips */}
      <div className="mb-3">
        <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5 block">
          Antwort erhalten?
        </label>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: 'yes', label: 'Ja',           Icon: CheckCircle2, on: 'bg-emerald-600 text-white border-emerald-600',  off: 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300' },
            { v: 'no',  label: 'Nein',         Icon: XCircle,      on: 'bg-red-600 text-white border-red-600',          off: 'bg-white text-slate-700 border-slate-200 hover:border-red-300' },
            { v: 'unknown', label: 'Noch offen', Icon: HelpCircle, on: 'bg-slate-700 text-white border-slate-700',      off: 'bg-white text-slate-700 border-slate-200 hover:border-slate-400' },
          ].map(({ v, label, Icon, on, off }) => (
            <button
              key={v}
              type="button"
              onClick={() => setResponseStatus(v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${responseStatus === v ? on : off}`}
              data-testid={`outcome-response-${v}`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Response details — only when response received */}
      {responseStatus === 'yes' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 pt-2 border-t border-emerald-200/60">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1 block">
              Antwortart
            </label>
            <select
              value={responseType}
              onChange={(e) => setResponseType(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              data-testid="outcome-response-type"
            >
              {RESPONSE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1 block">
              Antwortdatum
            </label>
            <Input
              type="date"
              value={responseDate}
              onChange={(e) => setResponseDate(e.target.value)}
              className="bg-white"
              data-testid="outcome-response-date"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1 block">
              Qualität
            </label>
            <select
              value={responseQuality}
              onChange={(e) => setResponseQuality(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              data-testid="outcome-response-quality"
            >
              {QUALITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <Button
        onClick={save}
        disabled={saving}
        className="bg-emerald-700 hover:bg-emerald-800 text-white"
        data-testid="outcome-save-btn"
        size="sm"
      >
        {saving ? <Loader2 size={13} className="mr-2 animate-spin" /> : <Save size={13} className="mr-2" />}
        Resonanz speichern
      </Button>
    </section>
  );
};

export default OutcomePanel;
